import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'

import styled from 'styled-components'

import Chip from '@eig-builder/core-ui/Chip'
import Typography from '@eig-builder/core-ui/Typography'

import useFetchAsJson from '@eig-builder/core-utils/hooks/useFetchAsJson'
import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'

import CustomIconButton from 'core/components/icon-button'
import InfoContent from 'core/components/info-content'
import Modal from 'core/components/modal'

import { AccountDetailsContext } from './context'

import AccountStatusChip from '../../account-status-chip'
import { getAccountName } from '../../../helpers/account-name-helper'
import FAAS_DECISION from '../../../enums/faas-decision'
import { getCrmApiOptions } from '../../../helpers/fetch-options-helper'
import TimeFormat from '../../../helpers/time-format'

const StyledContactInfo = styled.div`
  color: ${props => props.theme.palette.black.light};
  font-size: 15px;
  padding-bottom: ${props => props.theme.spacing()}px;

  ${props =>
    props.paddingTop &&
    `
    padding-top: ${props.theme.spacing()}px;
  `}

  .ctct-partner-summary-icon {
    padding: 0;
    margin-left: ${props => props.theme.spacing()}px;
    margin-top: ${props => -props.theme.spacing()}px;
  }
`

const CtctChip = styled(Chip)`
  height: 22px !important;
  margin-left: ${({ theme }) => theme.spacing(2)}px;
`

const constructLanderTypeString = (landerType, storeTypeName) => {
  const types = [landerType, storeTypeName].filter(Boolean)

  return `(${types.join(', ')})`
}

const getStoreTypeName = storeTypes => {
  const storeType = Array.isArray(storeTypes) && storeTypes.find(s => parseInt(s.store_type_id) === 2)

  return storeType && storeType.store_type_name
}

const ContactInfo = ({ account }) => {
  const [showCtctPartnerSummaryModal, setShowCtctPartnerSummaryModal] = useState(false)

  const { faasStatus } = useContext(AccountDetailsContext)

  const storeTypeOptions = getCrmApiOptions(account, '/v1.0/store/all')
  storeTypeOptions.dontShowErrorsInNotificationBar = true

  const [{ data: storeTypes }] = useFetchAsJson(storeTypeOptions)
  const [{ data: ctctAccount }] = useFetchAsJson()
  const [ctctPartnerSummaryReq] = useFetchAsJson()
  const [ctctAccountUserCredentialsReq] = useFetchAsJson()

  const storeTypeName = getStoreTypeName(storeTypes)

  const cityStateZipString = [account.city, account.state, account.zipCode].filter(i => i).join(', ')

  const newCtctAccountDate = moment.utc('2020-05-07T14:20:00')

  const isCtctV2 = ctctAccount && (
    (account.ctctV2 === true || (typeof account.ctctV2 === 'string' && account.ctctV2.toLowerCase() === 'true')) ||
    (account.ctctV2 === null && moment(account.created).utc().isSameOrAfter(newCtctAccountDate, 'day'))
  )

  const isCtctV3 = ctctAccount && (account.ctctV3 === true || (typeof account.ctctV3 === 'string' && account.ctctV3.toLowerCase() === 'true'))

  const showCtctPartnerSummary = ctctPartnerSummaryReq.isSuccess && ctctPartnerSummaryReq.data
  const showCtctAccountUserCredentials = ctctAccountUserCredentialsReq.isSuccess && ctctAccountUserCredentialsReq.data

  const openCtctPartnerSummaryModal = () => setShowCtctPartnerSummaryModal(true)
  const closeCtctPartnerSummaryModal = () => setShowCtctPartnerSummaryModal(false)

  const renderCtctPartnerSummaryInfo = () => {
    const partnerName = ctctPartnerSummaryReq.data.partnerName || ''
    const excludedPartners = ['roving', 'natsearch']

    if (excludedPartners.includes(partnerName.toLowerCase())) {
      return null
    }

    const { partnerUID, parentPartnerUID } = ctctPartnerSummaryReq.data

    const summaryString = [
      partnerName,
      partnerUID && partnerUID > 0 && 'Partner UID: ' + partnerUID,
      parentPartnerUID && parentPartnerUID > 0 && 'Parent Partner UID: ' + parentPartnerUID
    ].filter(Boolean).join(' - ')

    return <span>({summaryString}) <CustomIconButton
      customClasses='ctct-partner-summary-icon'
      onClick={openCtctPartnerSummaryModal}
      dataElementLocation={DataElementLocations.BODY}
      dataElementLabel='cst-account-detail--open-ctct-partner-summary'
      dataElementId='cst-account-detail--open-ctct-partner-summary'
    />
    </span>
  }

  const renderCtctAccountUserCredentials = () => {
    const externalProviders = [{ key: 'fb', name: 'Facebook' }, { key: 'google', name: 'Google' }]
    let userCredentials = ''
    if (Array.isArray(ctctAccountUserCredentialsReq.data)) {
      ctctAccountUserCredentialsReq.data.forEach(item => {
        if (item.type.toUpperCase() === 'EXTERNAL') {
          const provider = externalProviders.find(i => i.key === item.providerName.toLowerCase())
          if (provider) {
            userCredentials = `${userCredentials} (${provider.name} login${account.emailAdress.toLowerCase() !== item.name.toLowerCase() ? `: ${item.name}` : ``})`
          }
        }
      })
    }

    return <span>{userCredentials}</span>
  }

  return (
    <>
      <Typography variant='h2'>{getAccountName(account.firstName, account.lastName)}</Typography>
      <AccountStatusChip account={account} />{faasStatus === FAAS_DECISION.DECLINED && <AccountStatusChip account={{}} faasStatus={faasStatus} />}
      {isCtctV2 && <CtctChip label='CTCT V2' />}
      {isCtctV3 && <CtctChip label='CTCT V3' />}
      {account.ctctMigrationDate &&
        <StyledContactInfo paddingTop>
          Migrated on: <TimeFormat date={account.ctctMigrationDate} />
        </StyledContactInfo>}
      {ctctAccount && <>
        <StyledContactInfo paddingTop={!account.ctctMigrationDate}>
          CTCT Customer Id: {ctctAccount.customerId}
        </StyledContactInfo>
        <StyledContactInfo>
          CTCT Username: {ctctAccount.username}
        </StyledContactInfo>
        {ctctAccount.canceled &&
          <StyledContactInfo>
            CTCT Account Cancelled: Yes
          </StyledContactInfo>}
      </>}
      <StyledContactInfo>
        {account.emailAdress} {account.socialProvider && `(${account.socialProvider} login)`}{' '}
        {showCtctAccountUserCredentials && renderCtctAccountUserCredentials()}{' '}
        {constructLanderTypeString(account.landerType, storeTypeName)}{' '}
        {showCtctPartnerSummary && renderCtctPartnerSummaryInfo()}
      </StyledContactInfo>
      <StyledContactInfo>{account.phone}</StyledContactInfo>
      <StyledContactInfo>{account.streetAddress}</StyledContactInfo>
      <StyledContactInfo>{cityStateZipString}</StyledContactInfo>
      <StyledContactInfo>{account.countryCode}</StyledContactInfo>
      {showCtctPartnerSummaryModal &&
        <Modal
          dataElementPrefix='account-detail--open-ctct-partner-summary'
          open
          handleClose={closeCtctPartnerSummaryModal}
          title='CTCT Partner Summary'>
          <InfoContent data={ctctPartnerSummaryReq.data} />
        </Modal>}
    </>
  )
}

ContactInfo.propTypes = {
  account: PropTypes.object.isRequired
}

export default ContactInfo
